/* XNote proposal base script (layout/xp.liquid; defer, no dependencies).
   Cart API + drawer, sticky bar, in-view video, tabs, table switch, header menus, notify forms, the customer-video
   player (sections/xp-ugc). The Prime Day deal state is kept by snippets/xp-deal-head (inline, before first paint).
   Public API and data attributes: docs/build/foundation.md. */
(() => {
  'use strict';
  const d = document;
  const root = d.documentElement;
  const XP = (window.XP = window.XP || {});
  const $ = (s, el = d) => el.querySelector(s);
  const $$ = (s, el = d) => Array.from(el.querySelectorAll(s));
  const emit = (name, detail) => d.dispatchEvent(new CustomEvent(name, { detail }));
  const phone = matchMedia('(max-width: 767px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const money = (cents, currency) => {
    const whole = Math.round(cents) % 100 === 0;
    const code = (currency && typeof currency === 'object' ? currency.iso_code : currency) || 'USD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }).format(cents / 100);
  };
  XP.money = money;
  XP.escape = esc;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  const config = (() => { try { return JSON.parse(($('#xp-cart-config') || {}).textContent || '{}'); } catch (e) { return {}; } })();
  const S = config.strings || {};

  /* ---------- Dialog helpers: focus trap, backdrop close, return focus ---------- */
  function trap(dialog) {
    dialog.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, dialog).filter((el) => el.offsetParent !== null || el === d.activeElement);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && d.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && d.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    dialog.addEventListener('click', (e) => {
      if (e.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      root.classList.remove('xp-locked', 'xp-locked-gutter');
      const back = dialog._xpReturn;
      dialog._xpReturn = null;
      if (back && back.isConnected) back.focus({ preventScroll: true });
    });
  }
  /* Focus wrap for page dialogs (XP.trapFocus; QA a11y 2026-09-28: PDP zoom, Home demo). Two visually hidden,
     focusable sentinels at the dialog's edges hand focus to its last / first focusable element, so Tab never leaves
     an open modal for the browser chrome. Sentinels (not a Tab keydown check like trap()) because a native
     <video controls> reports the video as activeElement for every internal button. Page dialogs keep their own
     open/close/backdrop logic; this only wraps focus. Idempotent. */
  function trapFocus(dialog) {
    if (!dialog || dialog._xpFocusWrap) return;
    dialog._xpFocusWrap = true;
    const items = () => $$(`${FOCUSABLE}, video[controls]`, dialog).filter((el) => !el.hasAttribute('data-xp-sentinel') && el.getClientRects().length > 0 && !el.closest('[inert]'));
    const sentinel = (toLast) => {
      const s = d.createElement('span');
      s.tabIndex = 0;
      s.className = 'xp-vh';
      s.setAttribute('data-xp-sentinel', '');
      s.addEventListener('focus', () => {
        const list = items();
        const t = toLast ? list[list.length - 1] : list[0];
        if (t) t.focus({ preventScroll: true });
      });
      return s;
    };
    dialog.prepend(sentinel(true));
    dialog.append(sentinel(false));
  }
  XP.trapFocus = trapFocus;
  function showDialog(dialog, opener) {
    if (dialog.open) return;
    dialog._xpReturn = opener || d.activeElement;
    // Keep the scrollbar's gutter only when the page has a classic (space-taking) scrollbar, so locking never shifts
    // the page behind or leaves an empty strip (integration 2026-09-29).
    root.classList.toggle('xp-locked-gutter', innerWidth - root.clientWidth > 0);
    dialog.showModal();
    root.classList.add('xp-locked');
  }

  d.addEventListener('change', (event) => {
    if (event.target.matches('[data-xp-country-select]')) event.target.form.requestSubmit();
  });

  // Account disclosure: close on outside click or Escape, returning keyboard focus.
  d.addEventListener('click', (e) => {
    $$('[data-xp-account][open]').forEach((menu) => { if (!menu.contains(e.target)) menu.open = false; });
  });
  d.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    $$('[data-xp-account][open]').forEach((menu) => { menu.open = false; $('summary', menu).focus(); });
  });

  /* ---------- Cart API ---------- */
  async function request(path, body) {
    const res = await fetch(routeRoot + path, body
      ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
      : { headers: { Accept: 'application/json' } });
    let data = null;
    try { data = await res.json(); } catch (e) { /* empty body */ }
    if (!res.ok) {
      const err = new Error((data && (data.description || data.message)) || 'Request failed');
      err.status = res.status;
      throw err;
    }
    return data;
  }
  const normalize = (items) => (Array.isArray(items) ? items : [items]).map((i) => {
    const line = { id: Number(i.id), quantity: Math.max(1, Number(i.quantity) || 1) };
    if (i.selling_plan) line.selling_plan = Number(i.selling_plan);
    if (i.properties) line.properties = i.properties;
    return line;
  });

  const drawer = $('[data-xp-drawer]');
  const view = drawer && $('[data-xp-cart-view="drawer"]', drawer);
  const onCartPage = () => d.body.dataset.xpTemplate === 'cart';

  // Kaching owns v3's cart; the theme drawer is a fallback while the app is unavailable.
  function cartMode() {
    if (window.kachingCartApi && typeof window.kachingCartApi.openCart === 'function') return 'app';
    if (drawer) return 'drawer';
    return onCartPage() ? 'reload' : 'page';
  }

  // Rapid owns quantity pricing. Remove the superseded Prime Day code from existing carts.
  async function syncDealDiscount(cart, added = [], previous = cart) {
    const code = config.dealCode;
    if (!code) return cart;
    // Shopify's cart can expose codes through allocations rather than discount_codes.
    // Keep the previous cart when changing quantity: an ineligible code loses its allocation immediately.
    const applications = (previous.cart_level_discount_applications || []).concat(
      (previous.items || []).flatMap((i) => (i.line_level_discount_allocations || []).map((a) => a.discount_application)));
    const existing = [...new Set((previous.discount_codes || []).map((d) => d.code).concat(
      applications.filter((a) => a && a.type === 'discount_code').map((a) => a.title)))];
    const own = existing.some((c) => c.toUpperCase() === code.toUpperCase());
    if (!own) return cart;
    const codes = existing.filter((c) => c.toUpperCase() !== code.toUpperCase());
    return request('cart/update.js', { discount: codes.join(',') });
  }

  XP.cart = {
    get: () => request('cart.js'),
    async add(items, opts = {}) {
      const lines = normalize(items);
      await request('cart/add.js', { items: lines });
      let cart = await XP.cart.get();
      try { cart = await syncDealDiscount(cart, lines); }
      catch (err) { console.warn('Could not clear the previous promotion code', err); }
      updateCount(cart);
      emit('xp:cart:added', { items: lines, cart });
      if (opts.open !== false) {
        try { await XP.cart.open({ message: opts.message, added: lines, cart, opener: opts.opener }); }
        catch (err) { location.href = routeRoot + 'cart'; } // the add succeeded; show the cart page if the drawer fails
      }
      return cart;
    },
    async change(line, quantity) {
      const previous = await XP.cart.get();
      let cart = await request('cart/change.js', { line: Number(line), quantity: Number(quantity) });
      cart = await syncDealDiscount(cart, [], previous);
      updateCount(cart);
      emit('xp:cart:updated', { cart });
      return cart;
    },
    async open(opts = {}) {
      const mode = cartMode();
      if (mode === 'reload') { location.reload(); return; }
      if (mode === 'page') { location.href = routeRoot + 'cart'; return; }
      if (mode === 'app') {
        if (drawer && drawer.open) drawer.close();
        await window.kachingCartApi.refreshCart();
        window.kachingCartApi.openCart();
        return;
      }
      const cart = opts.cart || (await XP.cart.get());
      render(cart);
      // A page-supplied message (e.g. "XNote Voice added.") shows as the green banner. After any other add, a
      // confirmation built from the added lines is announced to screen readers only (visually hidden).
      const status = $('[data-xp-drawer-status]', drawer);
      const note = opts.message || (opts.added ? addedText(opts.added, cart) : '');
      status.textContent = note;
      status.hidden = !note;
      status.classList.toggle('xp-vh', Boolean(note) && !opts.message);
      if (note && status.id) drawer.setAttribute('aria-describedby', status.id); else drawer.removeAttribute('aria-describedby');
      showDialog(drawer, opts.opener);
      emit('xp:cart:open', { cart });
    },
    close() { if (drawer && drawer.open) drawer.close(); },
    render: (cart) => render(cart),
    config,
  };

  function updateCount(cart) {
    $$('[data-xp-cart-count]').forEach((el) => {
      const bump = String(cart.item_count) !== el.textContent && cart.item_count > 0;
      el.textContent = cart.item_count;
      el.hidden = !cart.item_count;
      if (bump) { el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump'); } // v2 micro-interaction
    });
    $$('[data-xp-cart-link]').forEach((a) => a.setAttribute('aria-label', cart.item_count ? `Cart, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}` : 'Cart'));
  }

  const icon = (kind) => kind === 'minus'
    ? '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M3.5 8h9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
    : '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M3.5 8h9M8 3.5v9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  // Line title shown in the drawer: "XNote Write" for the set (not the live product title, G-05), the product-page
  // names for notebooks and refills (config.titles, same as /cart), Voice per state.
  function displayTitle(item) {
    const voice = config.voice && item.product_id === config.voice.productId ? config.voice : null;
    if (voice) return voice.state === 'B' ? voice.preorderLabel : voice.saleLabel;
    if ((config.writeHandles || []).includes(item.handle)) return config.writeTitle;
    return (config.titles || {})[item.handle] || item.product_title;
  }
  // Line link: + ?view=… for supplies while their templates aren't assigned (config.views, setting xp_preview_views).
  function lineURL(item) {
    const view = (config.views || {})[item.handle];
    const url = String(item.url || '');
    if (!view || /[?&]view=/.test(url)) return url;
    return url + (url.includes('?') ? '&' : '?') + 'view=' + encodeURIComponent(view);
  }
  // "XNote Write added." / "XNote Write and XNote Standard Smart Notebook added." (strings.addedItems)
  function addedText(lines, cart) {
    const names = [...new Set(lines.map((l) => cart.items.find((i) => Number(i.variant_id || i.id) === Number(l.id))).filter(Boolean).map(displayTitle))];
    if (!names.length) return S.added || '';
    const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
    return (S.addedItems || '{items} added.').replace('{items}', list);
  }

  function lineHTML(item, index, cart) {
    const isWrite = (config.writeHandles || []).includes(item.handle);
    const voice = config.voice && item.product_id === config.voice.productId ? config.voice : null;
    const isPlan = !voice && ((config.planHandles || []).includes(item.handle) || Boolean(item.selling_plan_allocation));
    const title = displayTitle(item);
    let img = isWrite ? config.writeImage : item.image;
    if (img && !isWrite && /\/cdn\/shop\/|cdn\.shopify\.com/.test(img)) img += (img.includes('?') ? '&' : '?') + 'width=160';
    const meta = [];
    if (item.variant_title && item.variant_title !== 'Default Title') meta.push(`<p class="xp-line__meta">${esc(item.variant_title)}</p>`);
    // Discounts: Shopify's own line allocations (the theme never creates one), then the deal note while the pair
    // discount isn't applied yet (config.dealNote only while xp-deal shows; data-deal-only hides it after the end).
    const allocs = item.line_level_discount_allocations || [];
    allocs.forEach((al) => meta.push(`<p class="xp-line__meta xp-line__disc">${esc((al.discount_application && al.discount_application.title) || '')} <span class="xp-tnum">−${money(al.amount, cart.currency)}</span></p>`));
    if (isWrite && config.dealNote && item.quantity > 1 && !allocs.length) meta.push(`<p class="xp-line__meta xp-line__deal" data-deal-only="on">${esc(config.dealNote)}</p>`);
    if (isPlan) meta.push(`<p class="xp-line__meta">${esc(S.plan)}</p>`);
    if (voice && voice.state === 'B') {
      if (voice.terms) meta.push(`<div class="xp-line__meta xp-rte">${voice.terms}</div>`);
      if (voice.ph31) meta.push(`<p class="xp-line__meta">${voice.ph31}</p>`);
    }
    const name = esc(title);
    const href = esc(lineURL(item));
    return `<li class="xp-line" data-line="${index}">
      <a class="xp-line__media" href="${href}" tabindex="-1" aria-hidden="true">${img ? `<img src="${esc(img)}" alt="" width="80" height="80" loading="lazy">` : ''}</a>
      <div class="xp-line__info">
        <h3 class="xp-line__title"><a href="${href}">${name}</a></h3>${meta.join('')}
        <div class="xp-line__controls">
          <div class="xp-qty">
            <button type="button" class="xp-qty__btn" data-qty="${item.quantity - 1}" aria-label="${esc(S.decrease)}, ${name}">${icon('minus')}</button>
            <span class="xp-qty__value"><span class="xp-vh">${esc(S.quantity)} </span>${item.quantity}</span>
            <button type="button" class="xp-qty__btn" data-qty="${item.quantity + 1}" aria-label="${esc(S.increase)}, ${name}">${icon('plus')}</button>
          </div>
          <button type="button" class="xp-link xp-link--quiet" data-qty="0">${esc(S.remove)}<span class="xp-vh"> ${name}</span></button>
        </div>
      </div>
      <p class="xp-line__price"><span>${money(item.final_line_price, cart.currency)}</span>${item.original_line_price > item.final_line_price ? ` <s class="xp-line__was">${money(item.original_line_price, cart.currency)}</s>` : ''}</p>
    </li>`;
  }

  function render(cart) {
    if (!view) return;
    const foot = $('[data-xp-cart-foot]', drawer);
    const handles = cart.items.map((i) => i.handle);
    if (!cart.item_count) {
      // On the Write PDP the drawer has no "Shop XNote Write" button (QA fixes V20: it linked to the page the visitor is
      // on); "Notebooks & refills" is then the one action, as a secondary button.
      view.innerHTML = S.emptyCta
        ? `<div class="xp-cart-empty"><p>${esc(S.empty)}</p><a class="xp-btn xp-btn--primary" href="${esc(S.emptyCtaUrl)}">${esc(S.emptyCta)}</a><a class="xp-link" href="${esc(S.emptyLinkUrl)}">${esc(S.emptyLink)}</a></div>`
        : `<div class="xp-cart-empty"><p>${esc(S.empty)}</p><a class="xp-btn xp-btn--secondary" href="${esc(S.emptyLinkUrl)}">${esc(S.emptyLink)}</a></div>`;
      foot.hidden = true;
      return;
    }
    const hasWrite = handles.some((h) => (config.writeHandles || []).includes(h));
    const hasNotebook = handles.some((h) => (config.notebookHandles || []).includes(h));
    const extras = [];
    if (config.shipping) {
      const free = hasWrite || cart.total_price > config.shipping.threshold;
      extras.push(`<p class="xp-cart__ship">${esc(free ? config.shipping.free : config.shipping.under)}</p>`);
    }
    if (hasWrite && config.trust) extras.push(config.trust);
    // voice-r2 L5 (docs/build/voice-r2/PLAN.md §4.5): a Voice pre-order checks out on its own (config.voice.cartRule is
    // set only with the cart rule "block"); shown above checkout whenever Voice shares the cart with anything else.
    const vRule = config.voice && config.voice.cartRule;
    if (vRule && cart.items.some((i) => i.product_id === config.voice.productId) && cart.items.some((i) => i.product_id !== config.voice.productId)) extras.unshift(`<p class="xp-cart__rule" role="status">${esc(vRule)}</p>`);
    if (hasWrite || hasNotebook) {
      const offers = (config.crossSells || []).filter(Boolean);
      const picks = [];
      const refills = offers.find((o) => o.group === 'refills');
      if (refills && refills.available && !handles.includes(refills.handle)) picks.push(refills);
      const notebook = offers.filter((o) => o.group === 'notebook').find((o) => o.available);
      if (notebook && !handles.includes(notebook.handle)) picks.push(notebook);
      if (picks.length) extras.push(`<ul class="xp-xsells" role="list">${picks.slice(0, 2).map((o) => `<li><button type="button" class="xp-xsell" data-xp-add="${esc(o.id)}"><span class="xp-xsell__plus" aria-hidden="true"></span>${esc(o.label)}</button></li>`).join('')}</ul>`);
    }
    view.innerHTML = `<ul class="xp-lines" role="list">${cart.items.map((item, i) => lineHTML(item, i + 1, cart)).join('')}</ul>${extras.length ? `<div class="xp-cart-extras">${extras.join('')}</div>` : ''}`;
    $('[data-xp-cart-subtotal]', drawer).textContent = money(cart.total_price, cart.currency);
    const discounts = $('[data-xp-cart-discounts]', drawer);
    const apps = cart.cart_level_discount_applications || [];
    if (discounts) {
      discounts.innerHTML = apps.map((a) => `<li><span>${esc(a.title || '')}</span><span class="xp-tnum">−${money(a.total_allocated_amount, cart.currency)}</span></li>`).join('');
      discounts.hidden = !apps.length;
    }
    foot.hidden = false;
  }

  async function changeLine(button) {
    const row = button.closest('[data-line]');
    const error = $('[data-xp-cart-error]', drawer);
    view.setAttribute('aria-busy', 'true');
    error.hidden = true;
    const index = $$('button', row).indexOf(button);
    try {
      const cart = await XP.cart.change(row.dataset.line, button.dataset.qty);
      render(cart);
      const sameRow = Number(button.dataset.qty) > 0 && $(`[data-line="${row.dataset.line}"]`, view);
      const again = sameRow && $$('button', sameRow)[index];
      (again && !again.disabled ? again : $('[data-xp-drawer-close]', drawer)).focus();
    } catch (err) {
      error.textContent = err.message;
      error.hidden = false;
    } finally {
      view.setAttribute('aria-busy', 'false');
    }
  }

  function readQuantity(el) {
    const src = el.dataset.xpQtySource && $(el.dataset.xpQtySource);
    return Number((src && src.value) || el.dataset.xpQty || 1);
  }
  function showError(el, err) {
    const target = el.dataset.xpError && $(el.dataset.xpError);
    const text = el.dataset.xpErrorText || S.addError || err.message;
    if (target) { target.textContent = text; target.hidden = false; }
    else if (drawer && drawer.open) { const box = $('[data-xp-cart-error]', drawer); box.textContent = text; box.hidden = false; }
    else {
      let note = el.nextElementSibling;
      if (!note || !note.matches('.xp-add-error')) {
        note = d.createElement('p');
        note.className = 'xp-form-msg xp-form-msg--error xp-add-error';
        note.setAttribute('role', 'alert');
        el.after(note);
      }
      note.textContent = text;
    }
    emit('xp:cart:error', { error: err, source: el });
  }
  async function addFrom(el, items) {
    if (el.getAttribute('aria-busy') === 'true') return;
    el.setAttribute('aria-busy', 'true');
    const errTarget = el.dataset.xpError && $(el.dataset.xpError);
    if (errTarget) errTarget.hidden = true;
    try {
      await XP.cart.add(items, { message: el.dataset.xpAddedMessage, opener: el });
    } catch (err) {
      showError(el, err);
    } finally {
      el.removeAttribute('aria-busy');
    }
  }

  d.addEventListener('click', (e) => {
    const add = e.target.closest('[data-xp-add], [data-xp-add-items]');
    if (add) {
      e.preventDefault();
      let items;
      try { items = add.dataset.xpAddItems ? JSON.parse(add.dataset.xpAddItems) : [{ id: add.dataset.xpAdd, quantity: readQuantity(add), selling_plan: add.dataset.xpSellingPlan }]; }
      catch (err) { return; }
      addFrom(add, items);
      return;
    }
    const cartLink = e.target.closest('[data-xp-cart-link]');
    if (cartLink && (cartMode() === 'drawer' || cartMode() === 'app')) { e.preventDefault(); XP.cart.open({ opener: cartLink }); return; }
    if (drawer && drawer.contains(e.target)) {
      if (e.target.closest('[data-xp-drawer-close]')) { drawer.close(); return; }
      const qty = e.target.closest('[data-qty]');
      if (qty) { changeLine(qty); return; }
    }
    const pageQty = e.target.closest('[data-xp-line-qty]');
    if (pageQty) {
      e.preventDefault();
      const line = pageQty.closest('[data-line]').dataset.line;
      XP.cart.change(line, pageQty.dataset.xpLineQty).then(() => location.reload(), () => { location.href = pageQty.href; });
      return;
    }
    const jump = e.target.closest('[data-xp-scroll-to]');
    if (jump) {
      const target = $(jump.dataset.xpScrollTo);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'start' });
      // Its field, else its button (e.g. Voice Preorder, not the policy link above it), else a link, else the target.
      const field = target.querySelector('input:not([type="hidden"]):not([disabled])') || target.querySelector('button:not([disabled])') || target.querySelector('a[href]') || target;
      if (field === target && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      setTimeout(() => field.focus({ preventScroll: true }), reduced.matches ? 0 : 450);
    }
  });

  d.addEventListener('submit', (e) => {
    const form = e.target;
    if (form.matches('[data-xp-add-form]')) {
      e.preventDefault();
      const data = new FormData(form);
      addFrom(e.submitter || form, [{ id: data.get('id'), quantity: data.get('quantity'), selling_plan: data.get('selling_plan') }]);
    } else if (form.matches('[data-xp-checkout]') && window.XP_PREVIEW) {
      e.preventDefault();
      let note = form.nextElementSibling;
      if (!note || !note.matches('.xp-preview-note')) {
        note = d.createElement('p');
        note.className = 'xp-form-msg xp-preview-note';
        note.setAttribute('role', 'status');
        form.after(note);
      }
      note.textContent = S.previewCheckout || 'Checkout is not connected in the local preview.';
    }
  });

  if (drawer) trap(drawer);

  /* ---------- Header: desktop menus, mobile sheet, CTA hide ---------- */
  function initMenus() {
    let current = null;
    let hoverTimer = 0;
    let openedByHover = false;
    const menuOf = (t) => d.getElementById(t.getAttribute('aria-controls'));
    const links = (menu) => $$('a[href]', menu);
    const close = (t, focus) => {
      t.setAttribute('aria-expanded', 'false');
      menuOf(t).hidden = true;
      if (current === t) current = null;
      if (focus) t.focus();
    };
    const open = (t) => {
      if (current && current !== t) close(current);
      t.setAttribute('aria-expanded', 'true');
      menuOf(t).hidden = false;
      current = t;
    };
    $$('[data-xp-menu-trigger]').forEach((t) => {
      const item = t.parentElement;
      const menu = menuOf(t);
      t.addEventListener('click', () => {
        if (t.getAttribute('aria-expanded') === 'true' && !openedByHover) close(t);
        else open(t);
        openedByHover = false;
      });
      item.addEventListener('keydown', (e) => {
        const list = links(menu);
        const i = list.indexOf(d.activeElement);
        if (e.key === 'ArrowDown') { e.preventDefault(); if (menu.hidden) open(t); (list[i + 1] || list[0]).focus(); }
        else if (e.key === 'ArrowUp' && i > -1) { e.preventDefault(); (list[i - 1] || list[list.length - 1]).focus(); }
        else if (e.key === 'Home' && i > -1) { e.preventDefault(); list[0].focus(); }
        else if (e.key === 'End' && i > -1) { e.preventDefault(); list[list.length - 1].focus(); }
      });
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(t); });
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => { if (current !== t) { open(t); openedByHover = true; } }, 60);
      });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => { if (current === t && !item.contains(d.activeElement)) { close(t); openedByHover = false; } }, 180);
      });
    });
    d.addEventListener('click', (e) => { if (current && !current.parentElement.contains(e.target)) close(current); });
    // Escape closes an open menu wherever focus is (a hover-opened menu too, WCAG 1.4.13); focus returns to the
    // trigger only when it was inside the menu item.
    d.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !current) return;
      const t = current;
      const inside = t.parentElement.contains(d.activeElement);
      if (inside) e.preventDefault();
      close(t, inside);
      openedByHover = false;
    });

    const sheet = $('[data-xp-sheet]');
    const burger = $('[data-xp-sheet-open]');
    if (sheet && burger) {
      trap(sheet);
      burger.addEventListener('click', () => { showDialog(sheet, burger); burger.setAttribute('aria-expanded', 'true'); });
      $('[data-xp-sheet-close]', sheet).addEventListener('click', () => sheet.close());
      sheet.addEventListener('close', () => burger.setAttribute('aria-expanded', 'false'));
      matchMedia('(min-width: 1051px)').addEventListener('change', (m) => { if (m.matches && sheet.open) sheet.close(); });
    }

    const cta = $('[data-xp-header-cta]');
    const guards = $$('[data-xp-hide-header-cta]');
    if (cta && guards.length && 'IntersectionObserver' in window) {
      const seen = new Set();
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => (en.isIntersecting ? seen.add(en.target) : seen.delete(en.target)));
        cta.classList.toggle('is-hidden', seen.size > 0);
        cta.classList.remove('xp-header__cta--await'); // server-rendered hidden state on buy-box-first PDPs (xp-header)
      });
      guards.forEach((g) => io.observe(g));
    } else if (cta) cta.classList.remove('xp-header__cta--await');
  }

  /* ---------- Header surface (Studio Light, docs/design-v3-light.md §1.4; Design v2 §4) ----------
     Only "ink" (a full-width .xp-s-ink section, or the footer) flips the header to night glass; every other surface,
     including a leftover data-surface="violet", reads as paper. data-top is studio | paper (never ink). 
     html.xp-scrolled after 24 px of scroll (glass header, 72 → 60 px bar). html[data-surface-now] = the surface under
     the header bar where it rests once stuck: the deepest [data-surface] element crossing a 1 px line at the bar's
     middle, "paper" for sections that declare none (legacy sections), "ink" over the footer, and html[data-top] while
     the line is still above <main> (page top: the header sits in flow under the announcement bar, so nothing crosses;
     this also resets the header after a jump back to the top). Integration pass (2026-09-28, the page groups' header
     requests): two more lines at the bar's top and bottom edges detect a seam passing through the bar; while one does
     (or while the section under the middle carries data-hdr="solid", e.g. Choose's paper | ink split stage),
     html.xp-hdr-dense makes the glass near-opaque and switches it without the background transition, so neither half
     of a paper/ink seam reads as mud and the flip never cross-fades through grey. */
  function initSurface() {
    let queued = false;
    const scrolled = () => { queued = false; root.classList.toggle('xp-scrolled', window.scrollY > 24); };
    addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(scrolled); } }, { passive: true });
    scrolled();
    if (!('IntersectionObserver' in window)) return;
    const main = $('#MainContent');
    const marked = $$('[data-surface]');
    const plain = main ? Array.from(main.children).filter((s) => !s.matches('[data-surface]') && !s.querySelector('[data-surface]')) : [];
    const footer = $('[data-xp-footer]');
    const targets = marked.concat(plain, footer || []);
    if (!targets.length) return;
    const surfaceOf = (el) => (el === footer || el.getAttribute('data-surface') === 'ink' ? 'ink' : 'paper');
    // Page top: paper, unless a studio-top page still opens on an unconverted full-width ink section (transition
    // safety: the transparent header then takes the night tokens instead of ink text on a dark stage).
    const firstMarked = main && main.querySelector('[data-surface]');
    const topSurface = () => (root.dataset.top === 'studio' && firstMarked && firstMarked.getAttribute('data-surface') === 'ink' ? 'ink' : 'paper');
    // Lines 0 / 1 / 2 = the bar's top edge, middle and bottom edge (px from the viewport top, once stuck).
    const hits = [new Set(), new Set(), new Set()];
    const last = ['', '', ''];
    let ys = [0, 30, 59];
    let ios = [];
    const deepest = (set) => {
      // The deepest / last target in document order under the line wins (a nested surface beats its section).
      let best = null;
      set.forEach((el) => { if (!best || best.compareDocumentPosition(el) & (Node.DOCUMENT_POSITION_FOLLOWING | Node.DOCUMENT_POSITION_CONTAINED_BY)) best = el; });
      return best;
    };
    const at = (k) => {
      const best = deepest(hits[k]);
      if (best) { last[k] = surfaceOf(best); return { s: last[k], el: best }; }
      // Nothing under the line: above <main> (page top) → the template's first surface; a gap mid-page keeps the last.
      if (!main || main.getBoundingClientRect().top > ys[k] || !last[k]) last[k] = topSurface();
      return { s: last[k], el: null };
    };
    const pick = () => {
      const top = at(0), mid = at(1), bottom = at(2);
      if (root.dataset.surfaceNow !== mid.s) root.dataset.surfaceNow = mid.s;
      const solid = Boolean(mid.el && mid.el.closest('[data-hdr="solid"]'));
      root.classList.toggle('xp-hdr-dense', top.s !== bottom.s || solid);
    };
    const observe = () => {
      ios.forEach((io) => io.disconnect());
      hits.forEach((h) => h.clear());
      const header = $('[data-xp-header]');
      // Where the header rests once stuck (Home polish round 2): the sticky section's `top` + the header's offset in it.
      let top = header ? header.getBoundingClientRect().top : 0;
      const sec = header && header.closest('.shopify-section, .xp-header-section');
      if (sec && getComputedStyle(sec).position === 'sticky') {
        const st = parseFloat(getComputedStyle(sec).top);
        if (!Number.isNaN(st)) top = st + (top - sec.getBoundingClientRect().top);
      }
      top = Math.max(0, top);
      const bar = parseFloat(getComputedStyle(root).getPropertyValue('--xp-bar-h-min')) || 60;
      ys = [Math.round(top + 1), Math.round(top + bar / 2), Math.round(top + bar - 2)];
      const h = window.innerHeight;
      ios = ys.map((y, k) => {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((en) => (en.isIntersecting ? hits[k].add(en.target) : hits[k].delete(en.target)));
          pick();
        }, { rootMargin: `-${y}px 0px -${Math.max(0, h - y - 1)}px 0px` });
        targets.forEach((el) => io.observe(el));
        return io;
      });
      // No synchronous pick(): each observer's first callback reports every target, so the state is set from real hits.
    };
    observe();
    addEventListener('resize', () => { clearTimeout(observe._t); observe._t = setTimeout(observe, 150); });
  }

  /* ---------- Sticky buy bar (§3.4) ---------- */
  function initSticky() {
    const bar = $('[data-xp-sticky]');
    if (!bar || !('IntersectionObserver' in window)) return;
    const host = $('[data-xp-sticky-host]');
    if (host && bar.parentElement !== host) host.appendChild(bar);
    const primaries = $$('[data-xp-primary-cta]').filter((el) => !bar.contains(el));
    const footer = $('[data-xp-footer]');
    const visible = new Set();
    const cookieBanner = () => { const b = d.getElementById('shopify-pc__banner'); return Boolean(b && b.offsetParent !== null); };
    const update = () => {
      const first = primaries[0];
      const passed = first ? first.getBoundingClientRect().bottom < 0 : window.scrollY > window.innerHeight * 0.6;
      const show = passed && visible.size === 0 && !cookieBanner();
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
      bar.setAttribute('aria-hidden', String(!show));
      const shown = show && getComputedStyle(bar).display !== 'none';
      root.classList.toggle('xp-sticky-on', shown);
      // PDP bar (kind 'add'): the header CTA would repeat it and link to the page itself (lead decision L4).
      root.classList.toggle('xp-sticky-hides-cta', shown && bar.hasAttribute('data-xp-sticky-hides-header-cta'));
      emit('xp:sticky:change', { visible: show });
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => (en.isIntersecting ? visible.add(en.target) : visible.delete(en.target)));
      update();
    });
    // [data-xp-sticky-yield] (additive, Home polish round 1): the bar also steps aside while such an element is on
    // screen (Home's pinned story frame, whose step copy would otherwise sit under the bar on phones).
    primaries.concat(footer || [], $$('[data-xp-sticky-yield]')).forEach((el) => io.observe(el));
    bar.hidden = false;
    if (!primaries.length) addEventListener('scroll', update, { passive: true });
    update();
    XP.sticky = { update };
  }

  /* ---------- Video (docs/messaging.md §10.3) ----------
     No video bytes before playback: sources keep preload="none" (or data-xp-src inside inactive tab panels) and
     only play() fetches them. play() runs from one IntersectionObserver: ≥ 50 % visible, after `load`, tab not
     hidden, never with prefers-reduced-motion or Save-Data (poster only). Posters of non-priority videos are
     data-xp-poster and are set when the video comes within one viewport (display:none videos never load one). */
  const NO_SOURCE = 3; // HTMLMediaElement.NETWORK_NO_SOURCE
  const inview = new Set();
  let playIO = null;
  let posterIO = null;
  const setPoster = (v) => { if (v.dataset.xpPoster) { v.poster = v.dataset.xpPoster; delete v.dataset.xpPoster; } };
  // QA 2026-09-29 (App, Business first-load budget): a poster outside the first screen is armed only after `load`, so
  // below-the-fold posters never compete with the LCP image; one already on the first screen is set at once.
  function lazyPoster(v) {
    if (!v.dataset.xpPoster) return;
    if (!('IntersectionObserver' in window)) { setPoster(v); return; }
    const arm = () => {
      if (!v.dataset.xpPoster) return;
      posterIO = posterIO || new IntersectionObserver((entries) => entries.forEach((en) => {
        if (!en.isIntersecting) return;
        posterIO.unobserve(en.target);
        setPoster(en.target);
      }), { rootMargin: '100% 0px' });
      posterIO.observe(v);
    };
    if (d.readyState === 'complete') { arm(); return; }
    const r = v.getBoundingClientRect();
    // On the first screen means in the viewport both ways (QA fixes T-03: a carousel slide beside the viewport, e.g. the
    // PDP gallery's 360 slide, fetched its poster before the LCP).
    if (r.width && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth) { setPoster(v); return; }
    addEventListener('load', arm, { once: true });
  }
  function play(v) {
    if (v._xpUserPaused) return; // paused with its pause toggle: stays paused until toggled again
    if (v.dataset.xpSrc || v.querySelector('source[data-xp-src]')) return; // still deferred (inactive tab)
    if (!v._xpAuto) { v._xpAuto = true; v.muted = true; } // automatic playback starts muted; controls may unmute later
    setPoster(v);
    if (v.preload === 'none') v.preload = 'auto';
    if (v.dataset.xpNeedsLoad || v.networkState === NO_SOURCE) { delete v.dataset.xpNeedsLoad; v.load(); }
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }
  const autoplayOK = () => !reduced.matches && !saveData && 'IntersectionObserver' in window;
  function startPlayback() {
    if (playIO || !autoplayOK()) return;
    playIO = new IntersectionObserver((entries) => entries.forEach((en) => {
      const v = en.target;
      v._xpIn = en.intersectionRatio >= 0.5;
      if (v._xpIn && !d.hidden && !reduced.matches) play(v); else if (!v.paused) v.pause();
    }), { threshold: [0, 0.5] });
    inview.forEach((v) => playIO.observe(v));
  }
  function watchVideo(v) {
    lazyPoster(v);
    if (!v.hasAttribute('data-xp-inview') || inview.has(v)) return;
    inview.add(v);
    if (playIO) playIO.observe(v);
  }
  function initVideos() {
    $$('video').forEach(watchVideo);
    d.addEventListener('visibilitychange', () => inview.forEach((v) => {
      if (d.hidden) v.pause();
      else if (playIO && v._xpIn && !reduced.matches) play(v);
    }));
    reduced.addEventListener('change', () => {
      if (reduced.matches) inview.forEach((v) => v.pause());
      else if (d.readyState === 'complete') startPlayback();
    });
    if (d.readyState === 'complete') startPlayback(); else addEventListener('load', startPlayback, { once: true });
    initPauseToggles();
    initFilms();
  }
  // Register a video added after load (idempotent): same poster and playback rules, one shared observer.
  XP.observeVideo = (v) => { v.setAttribute('data-xp-inview', ''); watchVideo(v); };

  /* Looping clips: pause toggle (WCAG 2.2.2 Pause, Stop, Hide). The button (xp-media) is invisible until it has
     keyboard focus (the owner asked for no visible pause buttons); a click on the clip toggles too. A paused clip
     stays paused (play() skips it) until toggled again. Hidden while clips never move (reduced motion, Save-Data). */
  function initPauseToggles() {
    const toggles = $$('[data-xp-pause]');
    if (!toggles.length) return;
    const sync = () => toggles.forEach((b) => { b.hidden = !autoplayOK(); });
    const toggle = (v, b) => {
      const paused = !v._xpUserPaused;
      v._xpUserPaused = paused;
      b.setAttribute('aria-pressed', String(paused));
      if (paused) v.pause();
      else if (playIO && v._xpIn && !d.hidden && !reduced.matches) play(v);
    };
    toggles.forEach((b) => {
      const v = b.parentElement && $('video', b.parentElement);
      if (!v) return;
      b.addEventListener('click', () => toggle(v, b));
      if (!v.closest('a, button, label, summary')) v.addEventListener('click', () => toggle(v, b));
    });
    sync();
    reduced.addEventListener('change', sync);
  }

  /* Click-to-play films (xp-media controls: true): at rest the poster and a centred play button, without the
     browser's control bar; the native controls appear on the first play (the button, the PDP chapter chips or
     in-view playback). Films inside a <dialog> (Home demo) keep their controls: the dialog opens on a click and
     starts the film itself. Without JS the markup keeps `controls` and the button stays hidden. */
  function initFilms() {
    $$('video[data-xp-film]').forEach((v) => {
      const btn = v.parentElement && $('[data-xp-play]', v.parentElement);
      if (!btn || v.closest('dialog')) return;
      const reveal = () => {
        if (v.controls) return;
        v.controls = true;
        btn.hidden = true;
      };
      if (!v.paused) return; // already playing (e.g. a chapter chip before init)
      v.controls = false;
      btn.hidden = false;
      v.addEventListener('play', reveal);
      btn.addEventListener('click', () => {
        reveal();
        setPoster(v);
        if (v.preload === 'none') v.preload = 'auto';
        if (v.dataset.xpNeedsLoad || v.networkState === NO_SOURCE) { delete v.dataset.xpNeedsLoad; v.load(); }
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
        v.focus({ preventScroll: true });
      });
    });
  }

  /* ---------- Tabs with lazy media (only the active panel's media can load) ---------- */
  // Moves data-xp-src/srcset (and, unless posters === false, data-xp-poster) into real attributes. Never loads
  // video bytes: a <source> that changed is marked data-xp-needs-load and play() calls load() when it plays.
  function hydrate(panel, posters = true) {
    $$('[data-xp-src], [data-xp-srcset], [data-xp-poster]', panel).forEach((el) => {
      if (posters && el.dataset.xpPoster) { el.poster = el.dataset.xpPoster; delete el.dataset.xpPoster; }
      if (el.dataset.xpSrcset) { el.srcset = el.dataset.xpSrcset; delete el.dataset.xpSrcset; }
      if (el.dataset.xpSrc) {
        el.src = el.dataset.xpSrc;
        delete el.dataset.xpSrc;
        const v = el.closest('video');
        if (v) v.dataset.xpNeedsLoad = '1';
      }
    });
  }
  function initTabs() {
    $$('[data-xp-tabs]').forEach((box) => {
      const tabs = $$('[role="tab"]', box);
      const select = (tab, focus, init) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const panel = d.getElementById(t.getAttribute('aria-controls'));
          if (!panel) return;
          panel.hidden = !on;
          // On init the active panel keeps its lazy posters (the poster observer sets them near the viewport).
          if (on) hydrate(panel, !init);
          else $$('video', panel).forEach((v) => v.pause());
        });
        if (focus) tab.focus();
        if (!init) emit('xp:tab:change', { tabs: box, tab });
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => select(t));
        t.addEventListener('keydown', (e) => {
          const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 }[e.key];
          if (next === undefined) return;
          e.preventDefault();
          select(tabs[(next + tabs.length) % tabs.length], true);
        });
      });
      select(tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0], false, true);
    });
  }

  /* ---------- Comparison tables on phones: XNote Write + one alternative (segmented switch) ---------- */
  function initSwitches() {
    $$('table[data-xp-switch]').forEach((table, n) => {
      const heads = $$('thead th', table);
      const alts = heads.slice(2);
      if (alts.length < 2) return;
      const seg = d.createElement('div');
      seg.className = 'xp-seg';
      seg.setAttribute('role', 'group');
      seg.setAttribute('aria-label', table.dataset.xpSwitch || 'Compare with');
      const show = (col) => {
        $$('tr', table).forEach((tr) => Array.from(tr.children).forEach((cell, i) => {
          if (i >= 2 && i !== col) cell.setAttribute('data-xp-col-hidden', ''); else cell.removeAttribute('data-xp-col-hidden');
        }));
        $$('button', seg).forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.col) === col)));
      };
      alts.forEach((th, i) => {
        const b = d.createElement('button');
        b.type = 'button';
        b.dataset.col = String(i + 2);
        b.textContent = th.textContent.trim();
        b.addEventListener('click', () => show(i + 2));
        seg.appendChild(b);
      });
      table.before(seg);
      show(2);
      table.dataset.xpSwitchReady = String(n);
    });
  }

  /* ---------- Footer accordions (phones only; heading + button disclosure) ----------
     Markup: <div data-xp-fold><h2>Label</h2><ul id="…">…</ul></div>. Without JS and above 767 px the heading is
     plain text and the list is open (no tab stop, heading semantics kept). On phones the heading text is wrapped
     in <button aria-expanded aria-controls> and the list starts collapsed. */
  const CHEV = '<svg class="xp-chev" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path d="m3 4.5 3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  function initFolds() {
    const folds = $$('[data-xp-fold]').map((box, n) => {
      const head = $('h2, h3, h4', box);
      const list = head && head.nextElementSibling;
      if (!list) return null;
      if (!list.id) list.id = `xp-fold-${n + 1}`;
      const label = head.textContent.trim();
      const btn = d.createElement('button');
      btn.type = 'button';
      btn.className = 'xp-fold__btn';
      btn.setAttribute('aria-controls', list.id);
      btn.innerHTML = esc(label) + CHEV;
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        list.hidden = !open;
      });
      return { head, list, btn, label };
    }).filter(Boolean);
    if (!folds.length) return;
    const sync = () => folds.forEach((f) => {
      if (phone.matches && !f.btn.isConnected) {
        f.head.textContent = '';
        f.head.appendChild(f.btn);
        f.btn.setAttribute('aria-expanded', 'false');
        f.list.hidden = true;
      } else if (!phone.matches && f.btn.isConnected) {
        f.head.textContent = f.label;
        f.list.hidden = false;
      }
    });
    phone.addEventListener('change', sync);
    sync();
  }

  /* ---------- Horizontal scrollers (phone card rows): keyboard-reachable while they actually scroll ----------
     data-xp-scroller="<accessible name>" on the scroll container. While it overflows it gets tabindex="0" and the
     name (a list keeps its list role; other elements get role="region"), so arrow keys can scroll it (WCAG 2.1.1). */
  function initScrollers() {
    const boxes = $$('[data-xp-scroller]');
    if (!boxes.length) return;
    const sync = () => boxes.forEach((b) => {
      const scrolls = b.scrollWidth > b.clientWidth + 1;
      const isList = /^(UL|OL)$/.test(b.tagName);
      if (scrolls) {
        b.tabIndex = 0;
        b.setAttribute('aria-label', b.dataset.xpScroller);
        if (!isList) b.setAttribute('role', 'region');
      } else if (b.hasAttribute('tabindex')) {
        b.removeAttribute('tabindex');
        b.removeAttribute('aria-label');
        if (!isList) b.removeAttribute('role');
      }
    });
    sync();
    if (d.fonts && d.fonts.ready) d.fonts.ready.then(sync);
    if ('ResizeObserver' in window) { const ro = new ResizeObserver(sync); boxes.forEach((b) => ro.observe(b)); }
    else addEventListener('resize', sync, { passive: true });
  }

  /* ---------- Notify / newsletter forms (Shopify customer form) ---------- */
  function setError(form, on) {
    const box = form.closest('[data-xp-notify]');
    const input = $('input[type="email"]', form);
    const msg = $('.xp-form-msg[role="alert"]', form);
    if (!msg) return;
    msg.textContent = on ? box.dataset.error : '';
    msg.classList.toggle('xp-form-msg--error', on);
    msg.hidden = !on;
    if (on) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid');
  }
  function setSuccess(form, on) {
    const ok = $('[data-xp-notify-success]', form);
    const row = $('.xp-notify__row', form);
    if (!ok || !row) return;
    ok.hidden = !on;
    row.hidden = on;
    if (on) setError(form, false);
  }
  function initNotify() {
    const forms = $$('[data-xp-notify]:not([data-xp-notify-static]) form');
    forms.forEach((form) => {
      const input = $('input[type="email"]', form);
      if (!input) return;
      form.noValidate = true;
      form.addEventListener('submit', (e) => {
        const value = input.value.trim();
        input.value = value;
        if (!value || !input.checkValidity() || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
          e.preventDefault();
          setError(form, true);
          input.focus();
          return;
        }
        const btn = $('button[type="submit"]', form);
        if (btn) btn.setAttribute('aria-busy', 'true');
      });
      input.addEventListener('input', () => { if (input.getAttribute('aria-invalid')) setError(form, false); });
    });
    const params = new URLSearchParams(location.search);
    if (params.get('customer_posted') === 'true') {
      const id = decodeURIComponent(location.hash.slice(1));
      const target = id && d.getElementById(id);
      const form = target && (target.matches('form') ? target : target.querySelector('form'));
      if (form && form.closest('[data-xp-notify]')) {
        forms.forEach((f) => setSuccess(f, f === form));
        const ok = $('[data-xp-notify-success]', form);
        ok.tabIndex = -1;
        // Chrome applies the #<form id> fragment after this runs and, because the form can't take focus, drops focus
        // to <body>. A focusable form receives that fragment focus instead and hands it straight to the message.
        form.tabIndex = -1;
        form.addEventListener('focus', () => ok.focus({ preventScroll: true }), { once: true });
        ok.focus({ preventScroll: true });
      }
      params.delete('customer_posted');
      const qs = params.toString();
      history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
    }
  }

  /* ---------- Customer videos (sections/xp-ugc; docs/design-v3-light.md §5.5) ----------
     Cards are buttons ([data-xp-ugc-open] with data-src). One <dialog data-xp-ugc-dialog> per section holds a
     <video controls playsinline preload="none">: the src is set when the dialog opens (a click, so playback is
     user-started) and cleared on close, so the 15–30 MB originals never load otherwise. Esc, the backdrop and
     "Close video" close it; focus returns to the card (trap). Never autoplays in the page. */
  function initUgc() {
    $$('[data-xp-ugc]').forEach((sec) => {
      const dialog = $('[data-xp-ugc-dialog]', sec);
      const video = dialog && $('video', dialog);
      if (!dialog || !video) return;
      trap(dialog);
      trapFocus(dialog);
      const stop = () => { video.pause(); video.removeAttribute('src'); video.load(); };
      dialog.addEventListener('close', stop);
      $$('[data-xp-ugc-close]', dialog).forEach((b) => b.addEventListener('click', () => dialog.close()));
      $$('[data-xp-ugc-open]', sec).forEach((card) => card.addEventListener('click', () => {
        const src = card.getAttribute('data-src');
        if (!src) return;
        video.setAttribute('src', src);
        const poster = card.getAttribute('data-poster');
        if (poster) video.setAttribute('poster', poster); else video.removeAttribute('poster');
        // The player is named by the dialog's aria-label "XNote video" (QA fixes T-04: copying the card's "Play video N
        // of 5" made the playing video announce itself as a play button).
        showDialog(dialog, card);
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      }));
    });
  }

  /* ---------- Deferred images (QA fixes T-03): img[data-xp-defer] with data-xp-src / data-xp-srcset get their real
     sources after `load`, once within one viewport, so below-the-fold media (press logos, review photos) never compete
     with the LCP. Each carries a <noscript> copy for no-JS (xp-base.css hides the empty original there). Home's own
     deferred() (xp-home.js) handles the same attribute; setting a source twice is a no-op. ---------- */
  function initDefer() {
    const imgs = $$('img[data-xp-defer]');
    if (!imgs.length) return;
    const set = (im) => {
      const pic = im.parentElement;
      if (pic && pic.tagName === 'PICTURE') $$('source[data-xp-srcset]', pic).forEach((so) => { so.srcset = so.dataset.xpSrcset; delete so.dataset.xpSrcset; });
      if (im.dataset.xpSrcset) { im.srcset = im.dataset.xpSrcset; delete im.dataset.xpSrcset; }
      if (im.dataset.xpSrc) { im.src = im.dataset.xpSrc; delete im.dataset.xpSrc; }
    };
    // Observe one box per group (a marquee's clones sit outside its clip, so they load with the visible logos).
    const groups = new Map();
    imgs.forEach((im) => {
      const box = im.closest('.xp-marquee, .xp-revwall__photos') || im;
      if (!groups.has(box)) groups.set(box, []);
      groups.get(box).push(im);
    });
    const go = () => {
      if (!('IntersectionObserver' in window)) { imgs.forEach(set); return; }
      const io = new IntersectionObserver((es) => es.forEach((e) => {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        (groups.get(e.target) || []).forEach(set);
      }), { rootMargin: '100% 0px' });
      groups.forEach((_, box) => io.observe(box));
    };
    if (d.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
  }

  /* ---------- Back to top (owner 2026-10-04): the button in layout/xp shows after about two screens of scrolling
     and sits above the sticky bar (--xp-bottom-offset). Smooth scroll unless reduced motion; focus moves to main. */
  function initToTop() {
    const btn = $('[data-xp-totop]');
    if (!btn) return;
    btn.hidden = false;
    let raf = 0;
    const update = () => {
      raf = 0;
      const show = window.scrollY > window.innerHeight * 2;
      btn.classList.toggle('is-visible', show);
      btn.tabIndex = show ? 0 : -1;
    };
    addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
    btn.addEventListener('click', () => {
      const still = root.classList.contains('xp-still');
      window.scrollTo({ top: 0, behavior: still ? 'auto' : 'smooth' });
      const main = d.getElementById('MainContent');
      if (main) main.focus({ preventScroll: true });
    });
    update();
  }

  const init = () => {
    initMenus();
    initSurface();
    initSticky();
    initToTop();
    initVideos();
    initTabs();
    initSwitches();
    initFolds();
    initScrollers();
    initNotify();
    initUgc();
    initDefer();
    emit('xp:ready', {});
  };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
